import api from "@/api";
import { defineStore } from "pinia";
import { Learner } from "@/interfaces/learner.interface";
import { NewLearner } from "@/interfaces/new-learner.interface";
import i18n from "@/plugins/i18n";
import { useFilterStore } from "./filterStore";
import { add, set } from "lodash";
import { useDataStore } from "./dataStore";
import { useLicenseStore } from "./licenseStore";

export const useLearnerStore = defineStore({
  id: "learnerStore",
  state: () => ({
    loader: {
      learner: false,
      emailValidation: false,
    },
    loadingMessage: "",
    schoolLearners: [] as Learner[],
    archiveOptions: false
  }),
  getters: {
    getarchiveOptions(): boolean {
      return this.archiveOptions;
    },
    getSchoolLearners(): Learner[] {
      return this.schoolLearners.filter(
        (learner) => learner.status === "active" || learner.status === "deactivated"
      );
    },
    getAllSchoolLearners(): Learner[] {
      return this.schoolLearners;
    },
    getAllActiveSchoolLearners(): Learner[] {
      return this.schoolLearners.filter(
        (learner) => learner.status === "active" || learner.status === "deactivated"
      );
    },
    getLearnerById: (state) => (learnerId: string) => {
      return state.schoolLearners.find((learner) => learner._id === learnerId);
    },
    getLearnersByClassId: (state) => (classId: string) => {
      return state.schoolLearners.filter(
        (learner) => learner.classes && learner.classes.some((cls) => cls._id === classId)
      );
    },
    getLearnersByGrade: (state) => (grade: number) => {
      return state.schoolLearners.filter((learner) => learner.grade === grade);
    },
    getCountOfLearnersByFilters: (state) => {
      const filterStore = useFilterStore();
      const selectedSchool = filterStore.selectedFilters.school;
      const classGroups = filterStore.selectedFilters.classGroup;
      const selectedGrade = filterStore.selectedFilters.gradeLevel;

      let filteredLearners = state.schoolLearners.filter(
        (l) => l.status === "active" || l.status === "deactivated"
      );

      if (selectedSchool) {
        filteredLearners = filteredLearners.filter(
          (learner) => learner.schoolId === selectedSchool
        );
      }

      if (classGroups.length > 0) {
        filteredLearners = filteredLearners.filter((learner) =>
          learner.classes &&
          learner.classes.some((cls) => classGroups.includes(cls._id))
        );
      } else {
        if (selectedGrade) {
          filteredLearners = filteredLearners.filter(
            (learner) => learner.grade === selectedGrade
          );
        }
      }

      return filteredLearners.length;
    },
    getLearnerAssessmentBySelectedSubject: (state) => (studentId: string) => {
      const filterStore = useFilterStore();
      const selectedSubject = filterStore.selectedSubject;
      const learner = state.schoolLearners.find((learner) => learner._id === studentId);
      return learner?.assessmentStates.find(
        (assessmentState) => assessmentState.subject.toLowerCase() === selectedSubject.name.toLowerCase()
      );
    }
  },
  actions: {
    setArchiveOptions(value: boolean) {
      this.archiveOptions = value;
    },
    async bulkUpdateLearners(learners: any) {
      const filterStore = useFilterStore();

      this.loader.learner = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.updateLearner"
      );

      try {
        const bulkUpdateLearnersResponse = await api.put(
          "v2/organisation/students/update-multiple",
          learners
        );

        if (bulkUpdateLearnersResponse.status !== 200) {
          throw new Error("Failed to update learners data from API.");
        }

        const updatedLearners = bulkUpdateLearnersResponse.data.data;

        if (updatedLearners?.length > 0) {
          await this.fetchSchoolLearners(filterStore.selectedFilters.school);
        }

        return updatedLearners;
      } catch (error: any) {
        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loadingMessage = "";
        this.loader.learner = false;
      }
    },
    async updateLearner(learnerId: any, updateClassDetails: any) {
      this.loader.learner = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.updateLearner"
      );

      try {
        const updateLearnerResponse = await api.put(
          `v2/organisation/students/update/${learnerId[0]}`,
          updateClassDetails
        );

        if (updateLearnerResponse.status !== 200) {
          throw new Error("Failed to update learner data from API.");
        }

        const updatedLearner = updateLearnerResponse.data.data;

        // Find the index of the learner in the schoolLearners array
        const learnerIndex = this.schoolLearners.findIndex(
          (learner) => learner._id === updatedLearner._id
        );

        // If the learner is found, update the object in the array
        if (learnerIndex !== -1) {
          this.schoolLearners.splice(learnerIndex, 1, {
            ...this.schoolLearners[learnerIndex],
            ...updatedLearner,
          });
        } else {
          console.error(
            "Learner not found in schoolLearners array, cannot update."
          );
        }

        return updatedLearner;
      } catch (error: any) {
        // if (error.code) {
        //   throw new Error(error.message);
        // }
        return false;
      } finally {
        this.loadingMessage = "";
        this.loader.learner = false;
      }
    },
    async bulkCreateLearners(learners: NewLearner[]) {
      this.loader.learner = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.bulkCreateLearners"
      );

      try {
        const bulkCreateLearnersResponse = await api.post(
          "v2/organisation/students/bulk",
          learners
        );

        if (bulkCreateLearnersResponse.status !== 201) {
          throw new Error("Failed to create learners data from API.");
        }

        const bulkCreateLearnersData = bulkCreateLearnersResponse.data.data;

        console.log("bulkCreateLearnersData: ", bulkCreateLearnersData);
        // this.schoolLearners.push(...bulkCreateLearnersData);

        return bulkCreateLearnersData;
      } catch (error: any) {
        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loadingMessage = "";
        this.loader.learner = false;
      }
    },
    async bulkArchiveUnarchiveLearners(type: string, learners: NewLearner[]) {
      this.loader.learner = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.bulkArchiveLearners"
      );

      try {
        const bulkArchiveLearnersResponse = await api.post(
          "v2/organisation/students/bulk/archive-unarchive",
          {
            type: type,
            users: learners,
          }
        );

        if (bulkArchiveLearnersResponse.status !== 200) {
          throw new Error("Failed to archive or unarchive learners.");
        }

        const bulkArchiveLearnersData = bulkArchiveLearnersResponse.data;
        console.log("bulkArchiveLearnersData: ", bulkArchiveLearnersData);

        return bulkArchiveLearnersData;
      } catch (error: any) {
        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loadingMessage = "";
        this.loader.learner = false;
      }
    },
    async createLearner(learner: NewLearner) {
      const licenseStore = useLicenseStore();

      if (licenseStore.selectedSubjectHasSeatsAvailable) {
        this.loader.learner = true;
        this.loadingMessage = i18n.global.t(
          "loaders.learner_store.createLearner"
        );

        try {
          const createLearnerResponse = await api.post(
            "v2/organisation/students/new",
            learner
          );

          if (createLearnerResponse.status !== 201) {
            throw new Error("Failed to create learner data from API.");
          }

          const createLearnerData = createLearnerResponse.data.data;
          this.schoolLearners.push(createLearnerData);

          // Determine which licenses to assign based on the subjects in the learner's classes
          const licensesToAssign: string[] = [];
          createLearnerData.classes.forEach((classGroup: any) => {
            const subjectId = classGroup.subject._id;
            const license = licenseStore.getLicenseForSubject(subjectId);
            if (license) {
              licensesToAssign.push(license._id);
            }
          });

          // Assign the licenses to the learner
          if (licensesToAssign.length > 0) {
            await licenseStore.assignLicenses(createLearnerData._id, licensesToAssign);
          }

          return createLearnerData;
        } catch (error: any) {
          if (error.code) {
            throw new Error(error.message);
          }
          return false;
        } finally {
          this.loadingMessage = "";
          this.loader.learner = false;
        }
      } else {
        throw new Error("No seats available for the selected subject.");
      }
    },
    async addLearnerToClass(learnerIds: string, classId: string) {
      this.loader.learner = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.bulkCreateLearners"
      );

      try {
        const addLearnerToClassResponse = await api.post(
          `v2/organisation/class-group/assign-learners`,
          { classId, learnerIds }
        );

        if (addLearnerToClassResponse.status !== 200) {
          throw new Error("Failed to add learner to class from API.");
        }

        const addLearnerToClassData = addLearnerToClassResponse.data.data;

        const dataStore = useDataStore();
        dataStore.fetchInitialData();

        // Find the index of the learner in the schoolLearners array
        const learnerIndex = this.schoolLearners.findIndex(
          (learner) => learner._id === addLearnerToClassData._id
        );

        // If the learner is found, update the object in the array
        if (learnerIndex !== -1) {
          this.schoolLearners.splice(learnerIndex, 1, {
            ...this.schoolLearners[learnerIndex],
            ...addLearnerToClassData,
          });
        } else {
          console.error(
            "Learner not found in schoolLearners array, cannot update."
          );
        }

        return addLearnerToClassData;
      } catch (error: any) {
        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loadingMessage = "";
        this.loader.learner = false;
      }
    },
    async fetchSchoolLearners(schoolId: string) {
      this.loader.learner = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.fetchSchoolLearners"
      );

      try {
        const learnersResponse = await api.get(
          `v2/organisation/students/school/${schoolId}`
        );

        if (learnersResponse.status !== 200) {
          throw new Error("Failed to fetch learners data from API.");
        }

        const learnersData = learnersResponse.data.data;
        this.schoolLearners = learnersData;
      } catch (error: any) {
        console.log("Error: ", error);

        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loadingMessage = "";
        this.loader.learner = false;
      }
    },
    async validateEmail(email: string) {
      this.loader.emailValidation = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.validateEmail"
      );

      try {
        const emailValidationResponse = await api.get(
          `/v2/organisation/students/validate-email?email=${encodeURIComponent(
            email
          )}`
        );

        if (emailValidationResponse.status !== 200) {
          throw new Error("Failed to validate email from API.");
        }

        const emailValidationData = emailValidationResponse.data.exists;
        return emailValidationData;
      } catch (error: any) {
        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loadingMessage = "";
        this.loader.emailValidation = false;
      }
    },
  },
  persist: true,
});
